"use client";

import { Note } from "@/types/note";
import { getNotes, deleteNote } from "@/lib/storage";
import { useEffect, useState } from "react";

interface HistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectNote: (note: Note) => void;
  onNotesChange: () => void;
}

export default function HistoryPanel({
  isOpen,
  onClose,
  onSelectNote,
  onNotesChange,
}: HistoryPanelProps) {
  const [notes, setNotes] = useState<Note[]>([]);

  useEffect(() => {
    if (isOpen) {
      setNotes(getNotes());
    }
  }, [isOpen]);

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm("Are you sure you want to delete this note?")) {
      deleteNote(id);
      setNotes(getNotes());
      onNotesChange();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateContent = (content: string, maxLength: number = 80) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-blue to-brand-cyan p-4 flex items-center justify-between">
            <h2 className="text-white font-semibold text-lg">
              📝 Note History
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Notes list */}
          <div className="flex-1 overflow-y-auto p-4">
            {notes.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                <div className="text-4xl mb-2">📭</div>
                <p>No notes yet</p>
                <p className="text-sm">Create your first note!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    onClick={() => {
                      onSelectNote(note);
                      onClose();
                    }}
                    className="bg-notepad-yellow border border-notepad-line rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-1 mb-1">
                          {note.category && (
                            <span className="text-xs bg-brand-blue/10 text-brand-blue px-2 py-0.5 rounded-full">
                              {note.category}
                            </span>
                          )}
                          {note.occasion && (
                            <span className="text-xs bg-brand-cyan/20 text-gray-700 px-2 py-0.5 rounded-full">
                              {note.occasion}
                            </span>
                          )}
                        </div>
                        {note.topic && (
                          <h3 className="font-medium text-gray-800 text-sm truncate">
                            {note.topic}
                          </h3>
                        )}
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {truncateContent(note.content) || "Empty note"}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatDate(note.updatedAt)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleDelete(e, note.id)}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:bg-red-50 p-1.5 rounded transition-all"
                        title="Delete note"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
